import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchPaymentHistory } from "../../Redux/ReduxSlices/paymentSlice";

const FIRST_SHOW_DELAY = 20 * 1000; // 20 seconds

const CLOSE_DELAYS = {
  1: 5 * 60 * 1000, // 5 minutes
  2: 30 * 60 * 1000, // 30 minutes
};

export default function PaymentRequiredModal({
  hasPaymentRequired,
  showModal,
  setShowModal,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    data: paymentResp,
    loading: paymentHistoryLoading,
    error: paymentHistoryError,
  } = useSelector((state) => state.userPayment);

  useEffect(() => {
    dispatch(fetchPaymentHistory());
  }, [dispatch]);

  // Check if user has made any payment before (monthly recharge is over)
  const hasMadePayment = paymentResp?.total < 1;

  useEffect(() => {
    if (!hasPaymentRequired) return;
    if (showModal) return; // already open, don't re-trigger timers

    const loginAt = Number(localStorage.getItem("loginAt")) || Date.now();

    // Save login time once
    if (!localStorage.getItem("loginAt")) {
      localStorage.setItem("loginAt", String(loginAt));
    }

    const nextShowAt =
      Number(localStorage.getItem("paymentModalNextShowAt")) || 0;

    const now = Date.now();

    // 🔹 FIRST TIME: show after 20 seconds from login
    if (!nextShowAt) {
      const remaining = FIRST_SHOW_DELAY - (now - loginAt);

      if (remaining <= 0) {
        setShowModal(true);
      } else {
        const timer = setTimeout(() => setShowModal(true), remaining);
        return () => clearTimeout(timer);
      }
      return;
    }

    // 🔹 AFTER CLOSE: respect 5 min / 30 min logic
    if (now >= nextShowAt) {
      setShowModal(true);
    }
  }, [hasPaymentRequired, showModal, setShowModal]);

  const handleDismiss = () => {
    const closeCount =
      Number(localStorage.getItem("paymentModalCloseCount")) || 0;

    const newCount = closeCount + 1;
    localStorage.setItem("paymentModalCloseCount", String(newCount));

    const delay = CLOSE_DELAYS[newCount] ?? CLOSE_DELAYS[2];
    localStorage.setItem("paymentModalNextShowAt", String(Date.now() + delay));

    setShowModal(false);
  };

  const handlePayment = () => {
    setShowModal(false);
    navigate("/pricing");
  };

  return (
    <div className="p-3 sm:p-8">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          {/* Conditional heading based on payment history */}
          {!hasMadePayment ? (
            <h2 className="text-lg text-center sm:text-2xl font-bold text-gray-900">
              Monthly Recharge Required 🔄
            </h2>
          ) : (
            <h2 className="text-lg text-center sm:text-2xl font-bold text-gray-900">
              Congratulations! 🎉 You've successfully sent your first 50 emails.
            </h2>
          )}

          {/* Conditional subheading */}
          {!hasMadePayment ? (
            <p className="text-sm text-center sm:text-lg font-semibold text-gray-700 mt-2">
              Your monthly plan has expired. Please recharge to continue your
              campaign.
            </p>
          ) : (
            <p className="text-sm text-center sm:text-lg font-semibold text-gray-700 mt-2">
              Continue with payment to send the remaining emails.
            </p>
          )}
        </div>
      </div>

      {/* Conditional description */}
      {!hasMadePayment ? (
        <p className="mt-3 text-center text-sm sm:text-base text-gray-600">
          Your monthly recharge period is over. To continue sending emails and
          keep your campaigns active, please recharge now. After completing the
          payment, go to the campaign you want to resume, click the{" "}
          <strong>'Resume Campaign'</strong> button — one credit will be
          deducted, and your campaign will start.
        </p>
      ) : (
        <p className="mt-3 text-center text-sm sm:text-base text-gray-600">
          You've reached the limit of 50 free emails. To send the remaining
          emails in your campaigns and continue using our services, please
          proceed with payment.
        </p>
      )}

      <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:gap-3">
        <button
          onClick={handlePayment}
          className="w-full rounded-md main-button px-4 py-2 text-white font-medium hover:bg-blue-700 transition"
        >
          {hasMadePayment ? "Recharge Now" : "Go to payment"}
        </button>
      </div>
    </div>
  );
}
